/**
 * Loading Spinner Component
 * 
 * Reusable loading indicator with multiple size variants.
 * Used throughout the application for consistent loading states.
 * 
 * @example
 * <LoadingSpinner />
 * <LoadingSpinner size="lg" />
 * <LoadingSpinner size="sm" className="text-blue-500" />
 */

import React from 'react';

// =============================================================================
// TYPES
// =============================================================================

interface LoadingSpinnerProps {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Additional CSS classes */
  className?: string;
  /** Accessible label for screen readers */
  label?: string;
}

// =============================================================================
// SIZE CONFIGURATIONS
// =============================================================================

const sizeClasses = {
  sm: 'h-4 w-4 border-2',
  md: 'h-8 w-8 border-3',
  lg: 'h-12 w-12 border-4',
  xl: 'h-16 w-16 border-4',
} as const;

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * LoadingSpinner Component
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  className = '',
  label = 'Loading...',
}) => {
  return (
    <div
      className={`
        animate-spin rounded-full 
        border-haat-red border-t-transparent
        ${sizeClasses[size]}
        ${className}
      `}
      role="status"
      aria-label={label}
    >
      <span className="sr-only">{label}</span>
    </div>
  );
};

// =============================================================================
// FULL PAGE LOADING
// =============================================================================

interface FullPageLoadingProps {
  /** Loading message */
  message?: string;
}

/**
 * Full page loading overlay
 * Used for initial app loading and route transitions
 */
export const FullPageLoading: React.FC<FullPageLoadingProps> = ({
  message = 'Loading...',
}) => {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-haat-bg-light">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-haat-gray text-sm">{message}</p>
    </div>
  );
};

export default LoadingSpinner;

